import { Component, OnInit, OnDestroy } from '@angular/core';
import { LocalStorageService } from '../../service/local-storage.service';
import { Router } from '@angular/router';
import { CheckinService } from '../../service/checkin.service';
import { CheckinResultModel } from '../../models/checkinresult.model';
import { CheckinRequestModel } from '../../models/checkinrequest.model';
import { TranslateService } from '@ngx-translate/core';
import { AppointmentModel } from 'src/app/models/appointment.model';
import { EMPTY, catchError, finalize } from 'rxjs';
import { SettingsModel } from 'src/app/models/setting.model';
import { SpinnerService } from 'src/app/service/spinner.service';

@Component({
  selector: 'app-checkin',
  templateUrl: './checkin.component.html',
  styleUrls: ['./checkin.component.scss']
})
export class CheckinComponent implements OnInit, OnDestroy {
  checkInSuccess = false;
  noAppointments = false;
  token: string = '';
  logo: string = '';
  background: string = '';
  errorMessage: string = '';
  displaySsn: string = '';
  currentCentury: string = new Date().getFullYear().toString().slice(0, 2);
  lastCentury: number = parseInt(this.currentCentury) - 1;
  fontStyle = {};
  buttonStyle = {};
  checkInResult = new CheckinResultModel;
  checkInRequest = new CheckinRequestModel;
  timerToClose: NodeJS.Timeout | number | null = null;
  timerToClearSsn: NodeJS.Timeout | number | null = null;
  timerToMaskSsn: NodeJS.Timeout | number | null = null;
  millisecondsToCloseAppointments = 20000;
  millisecondsToClearSsn = 120000; 
  millisecondsToMaskSsn = 2000;

  constructor(
    private translate: TranslateService,
    private localstorageservice: LocalStorageService,
    private checkinservice: CheckinService,
    private router: Router,
    private spinnerService: SpinnerService
  ) { 
    this.setup();
  }
  ngOnInit() {
    window.addEventListener('keyup', this.handleKeyPress.bind(this));
  }

  ngOnDestroy() {
    window.removeEventListener('keyup', this.handleKeyPress.bind(this));
  }

  setup() {
    this.setStyles(this.localstorageservice.get('settings') as SettingsModel);
    this.token = this.localstorageservice.getToken();
    if (!this.token) {
      this.router.navigate(['']);
    }
  }
  checkin() {
    this.spinnerService.show(this.translate.instant('PAGES.CHECKIN.LOOKING_FOR_BOOKING'));
    this.checkinservice.checkin(this.checkInRequest, this.token).pipe(
      catchError(err => {
        this.errorMessage = err;
        this.spinnerService.hide();
        return EMPTY; // Ensures the observable chain is terminated in case of an error
      }),
      finalize(() => this.spinnerService.hide()) // Hides the spinner on completion or error
    ).subscribe((response: CheckinResultModel) => {
      this.noAppointments = response.status === 'NotFound';
      
      window.clearTimeout(<number>this.timerToClearSsn);
      this.checkInSuccess = true;
      this.checkInResult = response;
      
      this.timerToClose = window.setTimeout(() => {
        this.close();
      }, this.millisecondsToCloseAppointments);
    });
  }

  changelanguage(language: string) {
    this.translate.use(language);
  }

  addTosSsn(number: string) {
    if (this.checkInRequest.ssn.length < 12) {
      this.checkInRequest.ssn += number;
      this.maskSsn(number);
    }
    if (this.checkInRequest.ssn.length == 10 && (this.checkInRequest.ssn.slice(0,2) != this.currentCentury && this.checkInRequest.ssn.slice(0,2) != this.lastCentury.toString()))
    {
      this.errorMessage = this.translate.instant('ERROR.CHECKIN.SSN_NEEDS_YEAR') + " " + this.lastCentury + " " + this.translate.instant('ERROR.CHECKIN.OR') + " " + this.currentCentury;
    }

    window.clearTimeout(<number>this.timerToClearSsn);
    this.timerToClearSsn = window.setTimeout(() => {
      this.close();
    }, this.millisecondsToClearSsn);

    if (this.checkInRequest.ssn.length == 12) {
      if (this.checkinservice.validateSsn(this.checkInRequest.ssn)) {
        this.checkin();
      } else {
        this.errorMessage = this.translate.instant('ERROR.CHECKIN.INVALID_SSN');
      }
    }
  }

  removeFromsSsn() {
    this.errorMessage = '';
    this.checkInRequest.ssn = this.checkInRequest.ssn.slice(0, -1);
    this.maskSsn();
  }
  formatUtcDateTime(date: Date): string {
    const utcHours = date.getUTCHours();
    const utcMinutes = date.getUTCMinutes();
    return `${utcHours.toString().padStart(2, '0')}:${utcMinutes.toString().padStart(2, '0')}`;
  }
  formatAppointment(appointment: AppointmentModel): string {
    const treaterText = this.translate.instant('PAGES.CHECKIN.TREATER') + ': ' + appointment.treaterName + '\n';
    const timeText = this.translate.instant('PAGES.CHECKIN.TIME') + ': ' + this.formatUtcDateTime(new Date(appointment.from)) + ' - ' + this.formatUtcDateTime(new Date(appointment.to));
    return treaterText + timeText;
  }
  maskSsn(number?: string) {
    this.displaySsn = this.checkinservice.maskSsn(this.checkInRequest.ssn, number);
    if (number) {
      window.clearTimeout(<number>this.timerToMaskSsn);
      this.timerToMaskSsn = window.setTimeout(() => {
        this.displaySsn = this.checkinservice.maskSsn(this.checkInRequest.ssn);
      }, this.millisecondsToMaskSsn);
    } 
  }
  handleKeyPress(event: KeyboardEvent): void {
    const key = event.key;
    if (key >= '0' && key <= '9') {
      this.addTosSsn(key);
    } else if (key === 'Backspace') {
      this.removeFromsSsn();
    } else if (key === 'Escape') {
      this.clearSsn();
    }
  }
  setStyles(settings: SettingsModel) {
    if (!settings) return;
    this.logo = 'data:image/png;base64,' + settings.logo;
    this.background = 'linear-gradient(' + settings.backgroundColorStart + ', ' + settings.backgroundColorEnd + ')';
    this.fontStyle = {'color': settings.textColor };
    this.buttonStyle = {'color': settings.textColor, 'border':'1px solid #fff', 'font-size':'1.8rem'};
  }

  close() {
    window.clearTimeout(<number>this.timerToClose);
    this.checkInSuccess = false;
    this.noAppointments = false;
    this.errorMessage = '';
    this.clearSsn();
  }

  clearSsn() {
    this.errorMessage = '';
    this.checkInRequest.ssn = '';
    this.displaySsn = ''; 
    window.clearTimeout(<number>this.timerToMaskSsn);
  }

}
